import { useEffect, useState } from 'react';
import {
  useApi,
  discoveryApiRef,
  fetchApiRef,
} from '@backstage/core-plugin-api';
import { scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { createScaffolderFieldExtension } from '@backstage/plugin-scaffolder-react';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  FormHelperText,
  CircularProgress,
} from '@material-ui/core';
import type { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

type Category = { slug: string; name: string };

const Component = (props: FieldExtensionComponentProps<string>) => {
  const { onChange, rawErrors, required, formData, schema } = props;
  const discoveryApi = useApi(discoveryApiRef);
  const fetchApi = useApi(fetchApiRef);

  const [items, setItems] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const baseUrl = await discoveryApi.getBaseUrl('xvia-cms-api');
        const r = await fetchApi.fetch(`${baseUrl}/categories`);
        if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
        const data = (await r.json()) as { categories: Category[] };
        if (!cancelled) setItems(data.categories ?? []);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'erro ao listar categorias');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [discoveryApi, fetchApi]);

  const label = schema.title ?? 'Categoria';
  const hasError = !!rawErrors?.length;

  if (loading) {
    return (
      <FormControl margin="normal" fullWidth>
        <InputLabel>{label}</InputLabel>
        <CircularProgress size={20} />
      </FormControl>
    );
  }

  return (
    <FormControl
      margin="normal"
      required={required}
      error={hasError || !!error}
      fullWidth
    >
      <InputLabel>{label}</InputLabel>
      <Select
        value={formData ?? ''}
        onChange={e => onChange(String(e.target.value))}
      >
        <MenuItem value="">(nenhuma)</MenuItem>
        {items.map(c => (
          <MenuItem key={c.slug} value={c.slug}>
            {c.name}
          </MenuItem>
        ))}
      </Select>
      <FormHelperText>
        {error
          ? `Falha: ${error}`
          : schema.description ?? 'Categoria carregada do CMS X-Via'}
      </FormHelperText>
    </FormControl>
  );
};

export const XviaCategoryPickerExtension = scaffolderPlugin.provide(
  createScaffolderFieldExtension({
    name: 'XviaCategoryPicker',
    component: Component,
  }),
);
